import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '../../../components/AppImage';
import Button from '../../../components/UI/Button';
import Icon from '../../../components/AppIcon';
import { PersonalInfo } from '../types';

interface HeroSectionProps {
  personalInfo: PersonalInfo;
  stats?: {
    projectsCount: number;
    yearsOfExperience: number;
    githubCommits?: string;
    achievementCount?: number;
    happyClients?: string;
    latestCommit?: {
      message: string;
      repo: string;
      date: string;
      url: string;
    };
  };
}

const HeroSection = ({ personalInfo, stats }: HeroSectionProps) => {
  const getTypingTexts = () => {
    if (personalInfo?.typingTexts) {
      if (Array.isArray(personalInfo.typingTexts)) return personalInfo.typingTexts;
      if (typeof personalInfo.typingTexts === 'string') {
        return (personalInfo.typingTexts as string).split(',').map(s => s.trim());
      }
    }
    return ['Systems Architecture', 'Full-Stack Engineering', 'Strategic Technical Design'];
  };

  const typingTexts = getTypingTexts();

  return (
    <section className="relative min-h-screen flex items-center bg-[#FFF9EB] pt-32 pb-20 overflow-hidden" id="home">
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Left Content */}
          <div className="flex-1 w-full text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <h4 className="font-roboto text-2xl sm:text-4xl font-black text-[#0C2C39] tracking-tight">
                {personalInfo?.heroTitlePrefix || "Hello, I am"}
              </h4>

              <h1 className="font-roboto text-5xl sm:text-7xl lg:text-[100px] font-black text-[#0C2C39] leading-[1.1] tracking-tighter">
                {personalInfo?.name || "Yabets Kifle"}
              </h1>

              <div className="pt-8 space-y-3">
                {typingTexts.map((role, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
                    className="text-lg sm:text-2xl text-[#0C2C39]/80 font-bold font-roboto"
                  >
                    {role}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Image */}
          <div className="flex-1 w-full relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative aspect-square max-w-[500px] mx-auto flex items-center justify-center pt-8"
            >
              {/* White Circular Background (Cut-out Effect) */}
              <div className="absolute inset-4 rounded-full bg-white shadow-[0_40px_100px_rgba(0,0,0,0.08)] border border-[#0C2C39]/5 z-0" />

              {/* Floating Name Badge */}
              <div className="absolute top-4 right-0 py-2 px-6 bg-white rounded-full shadow-lg border border-[#0C2C39]/5 z-20">
                <span className="text-sm font-black text-[#0C2C39]">{personalInfo?.name?.toLowerCase() || "abeniezer kifle"}</span>
              </div>

              {/* Profile Image */}
              <div className="relative z-10 w-full h-full p-8">
                <Image
                  src={personalInfo?.profileImage}
                  alt={personalInfo?.name}
                  className="w-full h-full object-contain filter drop-shadow-[0_20px_40px_rgba(0,0,0,0.12)]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Extreme Right Floating Actions */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col space-y-4 p-4">
        {[
          { icon: 'BookOpen', label: 'Documentation' },
          { icon: 'Mic', label: 'Podcast' },
          { icon: 'Layout', label: 'Portfolio' },
          { icon: 'ExternalLink', label: 'External' }
        ].map((item, idx) => (
          <button
            key={idx}
            className="w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#0C2C39]/60 hover:text-[#0C2C39] hover:scale-110 transition-all border border-[#0C2C39]/5"
            title={item.label}
          >
            <Icon name={item.icon as any} size={20} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;